import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, switchMap, withLatestFrom, tap, take, of } from 'rxjs';
import { Router } from '@angular/router';
import { StorageService } from '@fund-base/services/storage/storage.service';
import { ErrorService } from '@fund-base/services/error/error.service';
import { setGlobalLoading, showToast } from '@fund-base/store/actions/ui.actions';
import { ToastType } from '@fund-base/types/ui/ui.types';
import { FundsService } from '@fund-base/services/funds/funds.service';
import {
  fetchFundingExperts,
  fetchFunds,
  fetchFundsAfter,
  fetchFundsThroughHistory,
  fetchFundsVisitors,
  fetchMoreFundingExperts,
  setAdvancedFilters,
  setFundingExperts,
  setFunds,
  setFundsFilterCount,
  setFundsFilterCountLoading,
  setFundsLoading,
  setFundsSearch,
  setUserHistory,
  setFundsPage,
  sendShareFund,
  fetchNumberFundsVisitors,
} from '@fund-base/store/actions/funds.actions';
import { fundsServiceInjectionToken } from '@fund-base/constants/funds/funds.constants';
import {
  selectFundingExperts,
  selectFunds,
  selectFundsCount,
  selectFundsFilters,
  selectFundsPage,
  selectFundsSearchPit,
  selectFundsSearchQuery,
} from '@fund-base/store/selectors/funds.selectors';
import { filtersFormatter } from '@fund-base/utils/helpers/general/general.helpers';
import { getVisitorSearchResultLimit } from '@fund-base/utils/helpers/visitors/visitors.helpers';
import { selectVisitorLimits } from '../selectors/visitors.selectors';

@Injectable()
export class FundsEffects {
  // fetch Funds
  fetchFunds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchFunds),
      switchMap(action => {
        // set Funds loading
        this.store.dispatch(setGlobalLoading({ loading: true }));
        this.store.dispatch(setAdvancedFilters({ filtersSearch: action?.filtersSearch }));

        // Check if pit exists delete it
        this.store.select(selectFundsSearchPit).pipe(take(1)).subscribe(pit => {
          if (pit) {
            this.fundsService.deletePit(pit).pipe(
              take(1),
              catchError(() => of(null))
            ).subscribe();
          }
        });

        return this.fundsService.getFunds(action?.filtersSearch, 0, 10).pipe(
          switchMap(response => {
            response.hasMore = response?.fundsCount > 10;

            // on success callback
            if (!!action?.onSuccess) action?.onSuccess(response);

            // Navigate to funding resources view after successful search
            this.router.navigate(['/admin/funds/funds-search']);

            // on success
            return [
              setFundsPage({ page: response.page }),
              setUserHistory({
                usersSearchHistory: response?.usersSearchHistory,
              }),
              setFundsSearch({
                searchQuery: response.searchQuery,
              }),
              setFunds({
                fundsList: response.fundsList,
                fundsCount: response.fundsCount,
                filtersCount: filtersFormatter(response.filtersCount),
                page: response.page,
                size: response.size,
                searchAfter: response?.searchAfter,
                pit: response?.pit,
              }),
              setGlobalLoading({ loading: false }),
            ];
          }),
          catchError(errorRes => {
            // get error
            const error = this.errorService.getErrorMessage(errorRes);

            // on error callback
            if (!!action?.onError) action?.onError(error);

            // on error
            return [setGlobalLoading({ loading: false }), showToast({ content: error, status: ToastType.failure })];
          })
        );
      })
    )
  );
  // fetchFundsFiltersCount$ = createEffect(
  //   () => this.actions$.pipe(
  //     ofType(fetchFundsFilterCount),
  //     switchMap((action) => {
  //         // set Funds loading
  //         this.store.dispatch(setFundsFilterCountLoading({filtersCountLoading: true}));
  //         return this.fundsService.getFundsFiltersCount(action?.query)
  //           .pipe(
  //             switchMap(response => {
  //               // on success callback
  //               if (!!action?.onSuccess) action?.onSuccess(response);
  //               // on success
  //               return [
  //                 setFundsFilterCount({
  //                   filtersFromQueries: response.filtersFromQueries
  //                 }),
  //                 setFundsFilterCountLoading({filtersCountLoading: false}),
  //               ]
  //             }),
  //             catchError(errorRes => {
  //               // get error
  //               const error = this.errorService.getErrorMessage(errorRes);

  //               // on error callback
  //               if (!!action?.onError) action?.onError(error);

  //               // on error
  //               return [
  //                 setFundsFilterCountLoading({filtersCountLoading: false}),
  //                 showToast({content: error, status: ToastType.failure})
  //               ];
  //             })
  //           );
  //       }
  //     )
  //   )
  // );

  // fetch Funds visitors
  fetchFundsVisitors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchFundsVisitors),
      withLatestFrom(this.store.select(selectVisitorLimits)),
      switchMap(([action, visitorLimits]) => {
        // set Funds loading
        this.store.dispatch(setGlobalLoading({ loading: true }));
        this.store.dispatch(setAdvancedFilters({ filtersSearch: action?.filtersSearch }));

        const allowed_search_results = getVisitorSearchResultLimit(visitorLimits);
        return this.fundsService.getFundsVisitors(action?.filtersSearch, 0, allowed_search_results).pipe(
          switchMap(response => {
            response.hasMore = response?.fundsCount > allowed_search_results;

            // on success callback
            if (!!action?.onSuccess) action?.onSuccess(response);

            // Navigate to visitor funding resources view after successful search
            this.router.navigate(['/visitors/funds-search']);

            // on success
            return [
              setFundsPage({ page: response.page }),
              setFundsSearch({
                searchQuery: response.searchQuery,
              }),
              setFunds({
                fundsList: response.fundsList,
                fundsCount: response.fundsCount,
                filtersCount: filtersFormatter(response.filtersCount),
                page: response.page,
                size: response.size,
                searchAfter: response?.searchAfter,
                pit: response?.pit,
              }),
              setGlobalLoading({ loading: false }),
            ];
          }),
          catchError(errorRes => {
            // get error
            const error = this.errorService.getErrorMessage(errorRes);

            // on error callback
            if (!!action?.onError) action?.onError(error);

            // on error
            return [setGlobalLoading({ loading: false }), showToast({ content: error, status: ToastType.failure })];
          })
        );
      })
    )
  );

  // fetch Number Funds visitors
  fetchNumberFundsVisitors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchNumberFundsVisitors),
      switchMap(action => {
        // set Funds loading
        this.store.dispatch(setGlobalLoading({ loading: true }));
        return this.fundsService.getNumberFundsVisitors().pipe(
          switchMap(response => {
            // on success callback
            if (!!action?.onSuccess) action?.onSuccess(response);

            // on success
            return [setGlobalLoading({ loading: false })];
          }),
          catchError(errorRes => {
            // get error
            const error = this.errorService.getErrorMessage(errorRes);

            // on error callback
            if (!!action?.onError) action?.onError(error);

            // on error
            return [setGlobalLoading({ loading: false }), showToast({ content: error, status: ToastType.failure })];
          })
        );
      })
    )
  );

  // fetch Funds Through History
  fetchFundsThroughHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchFundsThroughHistory),
      switchMap(action => {
        // set Funds loading
        this.store.dispatch(setGlobalLoading({ loading: true }));
        this.store.dispatch(setAdvancedFilters({ filtersSearch: action?.filtersSearch }));

        return this.fundsService.getFundsThroughHistory(action?.filtersSearch, action?.searchQuery).pipe(
          switchMap(response => {
            // on success callback
            if (!!action?.onSuccess) action?.onSuccess(response);

            // on success
            return [
              setFunds({
                fundsList: response.fundsList,
                fundsCount: response.fundsCount,
                filtersCount: filtersFormatter(response.filtersCount),
                page: 0,
                size: 10,
              }),
              setGlobalLoading({ loading: false }),
            ];
          }),
          catchError(errorRes => {
            // get error
            const error = this.errorService.getErrorMessage(errorRes);

            // on error callback
            if (!!action?.onError) action?.onError(error);

            // on error
            return [setGlobalLoading({ loading: false }), showToast({ content: error, status: ToastType.failure })];
          })
        );
      })
    )
  );

  fetchFundsAfter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchFundsAfter),
      withLatestFrom(
        this.store.select(selectFundsSearchQuery),
        this.store.select(selectFundsCount),
        this.store.select(selectFundsFilters),
        this.store.select(selectFunds),
        this.store.select(selectFundsPage)
      ),
      switchMap(([action, selectQuery, selectFundsCount, selectFundsFilters, funds]) => {
        // set Funds loading
        this.store.dispatch(setFundsLoading({ loading: true }));
        return this.fundsService.getFunds(action?.filtersSearch, action?.page, action?.size, action?.searchAfter, action?.pit).pipe(
          switchMap(response => {
            funds = funds ? funds : [];

            // Dedupe funds
            const uniqueFunds = response?.fundsList.filter(
              fund => !funds?.some(f => f.fund?.fb_id === fund.fund?.fb_id)
            );

            // Alert if there are duplicate funds
            if (uniqueFunds.length !== response?.fundsList.length) {
              console.error('Duplicate funds found');
            }

            const newFunds = [...funds, ...uniqueFunds];

            response.hasMore = newFunds.length < response?.fundsCount;
            if (!!action?.onSuccess) action?.onSuccess(response);

            // on success
            selectFundsCount = selectFundsCount ? selectFundsCount : 0;
            selectFundsFilters = selectFundsFilters ? selectFundsFilters : [];
            return [
              setFundsPage({ page: response.page }),
              setFunds({
                fundsCount: response.fundsCount,
                filtersCount: filtersFormatter(response.filtersCount),
                page: response.page,
                size: response.size,
                fundsList: newFunds,
                searchAfter: response?.searchAfter,
                pit: response?.pit,
              }),
              setFundsLoading({ loading: false }),
            ];
          }),
          catchError(errorRes => {
            // get error
            const error = this.errorService.getErrorMessage(errorRes);

            // on error callback
            if (!!action?.onError) action?.onError(error);

            // on error
            return [setFundsLoading({ loading: false }), showToast({ content: error, status: ToastType.failure })];
          })
        );
      })
    )
  );

  // fetch funding experts
  fetchFundingExperts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchFundingExperts),
      switchMap(action => {
        // set Funds loading
        this.store.dispatch(setFundsLoading({ loading: true }));
        return this.fundsService.getFundingExperts(action?.filters, action?.pagination).pipe(
          switchMap(response => {
            // on success callback
            if (!!action?.onSuccess) action?.onSuccess(response?.fundingExpertsList);

            // on success
            return [
              setFundingExperts({
                fundingExperts: {
                  fundingExpertsList: response?.fundingExpertsList,
                  totalCount: response?.totalCount,
                },
              }),
              setFundsLoading({ loading: false }),
            ];
          }),
          catchError(errorRes => {
            // get error
            const error = this.errorService.getErrorMessage(errorRes);

            // on error callback
            if (!!action?.onError) action?.onError(error);

            // on error
            return [setFundsLoading({ loading: false }), showToast({ content: error, status: ToastType.failure })];
          })
        );
      })
    )
  );
  // fetch funding experts
  fetchMoreFundingExperts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchMoreFundingExperts),
      withLatestFrom(this.store.select(selectFundingExperts)),
      switchMap(([action, fundingExpertsList$]) => {
        // set Funds loading
        this.store.dispatch(setFundsLoading({ loading: true }));
        return this.fundsService.getFundingExperts(action?.filters, action?.pagination).pipe(
          switchMap(response => {
            // on success callback
            if (!!action?.onSuccess) action?.onSuccess(response?.fundingExpertsList);

            // on success
            if (!!fundingExpertsList$) {
              response?.fundingExpertsList.push(...fundingExpertsList$);
            }
            return [
              setFundingExperts({
                fundingExperts: {
                  fundingExpertsList: response?.fundingExpertsList,
                  totalCount: response?.totalCount,
                },
              }),
              setFundsLoading({ loading: false }),
            ];
          }),
          catchError(errorRes => {
            // get error
            const error = this.errorService.getErrorMessage(errorRes);

            // on error callback
            if (!!action?.onError) action?.onError(error);

            // on error
            return [setFundsLoading({ loading: false }), showToast({ content: error, status: ToastType.failure })];
          })
        );
      })
    )
  );

  shareFund$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendShareFund),
      switchMap(action => {
        // get token

        // set auth loading
        this.store.dispatch(setFundsLoading({ loading: true }));

        // call authenticate from service
        return this.fundsService.shareFund(action?.body, action.languate).pipe(
          switchMap(response => {
            // on success callback
            if (!!action?.onSuccess) action?.onSuccess(response);

            // on success
            return [setFundsLoading({ loading: false })];
          }),
          catchError(errorRes => {
            // get error
            const error = this.errorService.getErrorMessage(errorRes);

            // on error callback
            if (!!action?.onError) action?.onError(error);

            // on error
            return [setFundsLoading({ loading: false })];
          })
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private storageService: StorageService,
    private errorService: ErrorService,
    @Inject(fundsServiceInjectionToken) private fundsService: FundsService
  ) {}
}
